import React from 'react';
import { Card, CardContent, Typography, Box, Avatar, Skeleton } from '@mui/material';
import { motion } from 'framer-motion';
import { DeviceConfiguration } from '../../constants/deviceConfigurations';

interface ConfigurationCardProps {
  configuration: DeviceConfiguration;
  selected: boolean;
  onClick: () => void;
  index: number;
  /** 🎧 해당 구성의 예상 가격 (관리자 설정 가격) */
  price?: number | null;
  /** 가격 로딩 중 여부 */
  priceLoading?: boolean;
}

export default function ConfigurationCard({ 
  configuration, 
  selected, 
  onClick, 
  index,
  price,
  priceLoading = false,
}: ConfigurationCardProps) {

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Card
        onClick={onClick}
        sx={{
          cursor: 'pointer',
          height: '100%',
          border: selected ? '2px solid #6366f1' : '1px solid rgba(0,0,0,0.08)',
          borderRadius: 4,
          backgroundColor: selected ? 'rgba(99, 102, 241, 0.04)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: selected 
              ? '0 20px 40px rgba(99, 102, 241, 0.15), 0 8px 16px rgba(99, 102, 241, 0.1)'
              : '0 20px 40px rgba(0,0,0,0.08), 0 8px 16px rgba(0,0,0,0.04)',
            backgroundColor: selected ? 'rgba(99, 102, 241, 0.08)' : 'rgba(255, 255, 255, 1)',
            borderColor: selected ? '#6366f1' : 'rgba(99, 102, 241, 0.2)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: selected 
              ? 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)'
              : 'transparent',
            transition: 'all 0.3s ease',
          }
        }}
      >
        <CardContent sx={{ 
          p: 3, 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center'
        }}>
          {/* 구성 이미지 */}
          <Avatar
            src={configuration.image}
            alt={configuration.name}
            sx={{
              width: 64,
              height: 64,
              mb: 2,
              backgroundColor: selected ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255, 255, 255, 0.9)',
              border: selected ? '2px solid #8b5cf6' : '2px solid rgba(99, 102, 241, 0.2)',
              transition: 'all 0.3s ease',
              '& img': {
                objectFit: 'contain',
                padding: '8px',
              }
            }}
          />

          {/* 구성명 */}
          <Typography 
            variant="h6" 
            sx={{ 
              fontSize: '1.1rem', 
              fontWeight: 700,
              color: selected ? '#6366f1' : '#1f2937',
              lineHeight: 1.3,
              mb: 1.5,
            }}
          >
            {configuration.name}
          </Typography>
          
          {/* 구성 설명 */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: selected ? '#6366f1' : '#6b7280',
              fontWeight: 500,
              fontSize: '0.9rem',
              lineHeight: 1.4,
              mb: 2,
            }}
          >
            {configuration.description}
          </Typography>

          {/* 💰 예상 시세 표시 영역 */}
          <Box sx={{ mt: 'auto', width: '100%' }}>
            {priceLoading ? (
              // 가격 로딩 중
              <Skeleton 
                variant="rounded" 
                width="100%" 
                height={36} 
                sx={{ borderRadius: 2 }}
              />
            ) : price && price > 0 ? (
              // 가격 정보 있음
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1,
                  px: 1.5,
                  borderRadius: 2,
                  background: selected 
                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)'
                    : 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.08) 100%)',
                  border: selected 
                    ? '1px solid rgba(99, 102, 241, 0.3)'
                    : '1px solid rgba(16, 185, 129, 0.2)',
                  transition: 'all 0.3s ease',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 800,
                    fontSize: '0.95rem',
                    color: selected ? '#6366f1' : '#059669',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {price.toLocaleString()}원
                </Typography>
              </Box>
            ) : (
              // 가격 정보 없음 (미설정)
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1,
                  px: 1.5,
                  borderRadius: 2,
                  background: 'rgba(107, 114, 128, 0.06)',
                  border: '1px dashed rgba(107, 114, 128, 0.25)',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: '#9ca3af',
                  }}
                >
                  검수 후 알림 예정
                </Typography>
              </Box>
            )}
          </Box>

        </CardContent>
      </Card>
    </motion.div>
  );
}